/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: We use dangerouslySetInnerHTML for code highlighting */
'use client'

import { useRef, useEffect, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { codeToHtml } from 'shiki'
import { InstallSnippet, type PackageManager } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Optional per-step code block.
 * - `type: 'code'` renders a one-line syntax-highlighted snippet.
 * - `type: 'install'` renders an interactive InstallSnippet (copy-to-clipboard).
 */
export type HowItWorksStepCode =
  | { type: 'code'; code: string; language?: string }
  | { type: 'install'; package: string; command?: PackageManager }

/** Single step in the simple (horizontal arrow-flow) how-it-works layout. */
export interface HowItWorksSimpleStep {
  title: string
  /** Step description — supports Markdown. Keep to ~3 lines when steps carry code. */
  description: string
  /** Optional code or install block at the bottom of the step. Omit for a text-only step. */
  code?: HowItWorksStepCode
}

export interface HowItWorksSimpleProps {
  /** Section id for anchor links */
  id?: string
  /** Small label above heading */
  label?: string
  /** Section heading */
  heading?: string
  /** Section description — supports Markdown */
  description?: string
  /** Array of steps (3 recommended to match the 3-column layout) */
  steps?: HowItWorksSimpleStep[]
  className?: string
}

const defaultSteps: HowItWorksSimpleStep[] = [
  {
    title: 'Install the package',
    description:
      'Add MDXUI to your project with a single command. Works with `npm`, `yarn`, `pnpm`, or `bun`.',
  },
  {
    title: 'Import components',
    description:
      'Import the components you need from the library. Full TypeScript support included.',
  },
  {
    title: 'Build your page',
    description:
      'Compose components together to create beautiful interfaces. Customize as needed.',
  },
]

/** Inline code block with syntax highlighting — matches InstallSnippet height/styling */
function HighlightedCode({ code, language = 'typescript' }: { code: string; language?: string }) {
  const [darkHtml, setDarkHtml] = useState<string>('')
  const [lightHtml, setLightHtml] = useState<string>('')

  useEffect(() => {
    codeToHtml(code, { lang: language, theme: 'github-dark' }).then(setDarkHtml)
    codeToHtml(code, { lang: language, theme: 'github-light' }).then(setLightHtml)
  }, [code, language])

  return (
    <div className="h-11 flex items-center overflow-x-auto rounded-[var(--radius)] bg-muted px-4 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-border">
      <div
        className="text-sm [&_pre]:!m-0 [&_pre]:!bg-transparent [&_pre]:!whitespace-nowrap [&_pre]:!w-fit [&_code]:!bg-transparent dark:hidden"
        dangerouslySetInnerHTML={{ __html: lightHtml }}
      />
      <div
        className="hidden text-sm [&_pre]:!m-0 [&_pre]:!bg-transparent [&_pre]:!whitespace-nowrap [&_pre]:!w-fit [&_code]:!bg-transparent dark:block"
        dangerouslySetInnerHTML={{ __html: darkHtml }}
      />
    </div>
  )
}

/**
 * HowItWorksSimple — horizontal steps connected by arrows (the simple default).
 *
 * One prop-driven layout covering text-only, inline-code, and install steps
 * (formerly HowItWorks2 / HowItWorks2Code / HowItWorks2CodeInstall). Each step
 * may carry an optional `code` block (`type: 'code'` or `type: 'install'`); omit
 * it for a clean text-only step. When ANY step has code, the cards switch to the
 * aligned layout (numbered, fixed min-heights, code pinned to the bottom).
 *
 * **Layout:**
 * ```
 * ┌────────────┐  →  ┌────────────┐  →  ┌────────────┐
 * │ 01 Title   │     │ 02 Title   │     │ 03 Title   │
 * │ Desc       │     │ Desc       │     │ Desc       │
 * │ [code]     │     │ [code]     │     │ [code]     │
 * └────────────┘     └────────────┘     └────────────┘
 * ```
 *
 * @example
 * ```tsx
 * <HowItWorksSimple
 *   heading="Just 3 Steps"
 *   steps={[
 *     { title: 'Install', description: 'Add the package', code: { type: 'install', package: 'mdxui', command: 'pnpm add' } },
 *     { title: 'Import', description: 'Pull in components', code: { type: 'code', code: "import { Hero } from 'mdxui'" } },
 *     { title: 'Build', description: 'Compose your page', code: { type: 'code', code: '<Hero title="Welcome" />', language: 'tsx' } },
 *   ]}
 * />
 * ```
 */
const HowItWorksSimple = ({
  id,
  label = 'How to implement',
  heading = 'Just 3 Steps to get Started',
  description = 'Get up and running quickly with our streamlined setup process. From installation to production in minutes.',
  steps = defaultSteps,
  className,
}: HowItWorksSimpleProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  // When any step carries code, switch to the aligned card layout (numbered +
  // fixed min-heights + a pinned code slot). Otherwise render the clean text-only base.
  const hasCode = steps.some((s) => s.code)

  return (
    <motion.section
      id={id}
      ref={sectionRef}
      className={cn('bg-background py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto flex flex-col items-center justify-center gap-10">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center gap-6 text-center"
          variants={headerAnimation}
        >
          <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
          <h2 className="text-3xl font-semibold sm:text-4xl text-balance">{heading}</h2>
          <div className="max-w-3xl text-balance">
            <Markdown size="base">{description}</Markdown>
          </div>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          className="relative grid grid-cols-1 md:grid-cols-3"
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              className={cn(
                'relative flex flex-col border border-border',
                hasCode ? 'p-4 pb-10 sm:p-8 md:p-10' : 'gap-4 p-8 sm:p-10',
              )}
              variants={itemAnimation}
            >
              {hasCode && (
                <span className="text-3xl font-semibold text-muted-foreground/40 leading-none">
                  {String(index + 1).padStart(2, '0')}
                </span>
              )}

              <h3 className={cn('text-xl font-semibold', hasCode && 'mt-4 min-h-[1.75rem]')}>
                {step.title}
              </h3>

              <div
                className={cn(
                  'prose prose-sm max-w-none text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
                  hasCode && 'mt-2 min-h-[4.5rem] line-clamp-3',
                )}
              >
                <ReactMarkdown>{step.description}</ReactMarkdown>
              </div>

              {/* Code / install slot — pinned to the bottom for alignment */}
              {hasCode && (
                <div className="mt-4 flex-grow flex flex-col justify-end">
                  {step.code ? (
                    step.code.type === 'install' ? (
                      <InstallSnippet
                        package={step.code.package}
                        command={step.code.command || 'pnpm add'}
                        variant="muted"
                        className="w-full"
                      />
                    ) : (
                      <HighlightedCode code={step.code.code} language={step.code.language} />
                    )
                  ) : (
                    <div className="h-11" />
                  )}
                </div>
              )}

              {/* Arrow connector - not on last item */}
              {index < steps.length - 1 && (
                <div className="absolute right-1/2 -bottom-5 z-10 flex size-10 translate-x-1/2 rotate-90 items-center justify-center rounded-full border border-border bg-background md:top-1/2 md:-right-5 md:translate-x-0 md:-translate-y-1/2 md:rotate-0">
                  <ArrowRight className="size-5 text-primary/80" />
                </div>
              )}
            </motion.div>
          ))}
          {/* Edge gradients */}
          <div className="pointer-events-none absolute left-0 z-10 h-full w-4 sm:w-10 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute right-0 z-10 h-full w-4 sm:w-10 bg-gradient-to-l from-background to-transparent" />
        </motion.div>
      </div>
    </motion.section>
  )
}

export { HowItWorksSimple }
